import { Canvas, useFrame } from '@react-three/fiber';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { BackSide, CanvasTexture, Group } from 'three';
import { PlayerCharacter } from '@/game/three/PlayerCharacter';
import { getBodyTint, getCharacterBodyPath, getMeshLayers } from '@/domain/meshMapping';
import { getItem } from '@/domain/items';
import type { EquippedMap } from '@/domain/types';

interface Props {
  equipment: EquippedMap;
  /** Optional rim-light colour (e.g. derived from highest equipped rarity). */
  glowColor?: string;
  /** Container size in px. */
  width?: number;
  height?: number;
}

const DRAG_SENSITIVITY = 0.012; // radians per pixel of drag
const IDLE_TURN_SPEED = 0.25;   // rad/s gentle turntable when not being dragged

/**
 * Mini Three.js canvas that renders the player as a live 3D character with
 * all currently-equipped items composited. Drag horizontally to rotate the
 * character around its Y axis for a 360° preview; release to let it idle-spin
 * gently.
 */
export function CharacterPortrait3D({ equipment, glowColor, width = 140, height = 180 }: Props) {
  const bodyPath = useMemo(() => getCharacterBodyPath(equipment), [equipment]);
  const layers = useMemo(() => getMeshLayers(equipment), [equipment]);
  const bodyTint = useMemo(() => getBodyTint(equipment), [equipment]);
  const lightningInHand = useMemo(() => {
    const wep = equipment.weapon;
    if (!wep) return false;
    return getItem(wep.itemId)?.attackVisual === 'lightning';
  }, [equipment]);

  // Rotation state lives in a ref so per-frame updates don't trigger re-renders.
  const rotationYRef = useRef(0);
  const dragStateRef = useRef<{ active: boolean; lastX: number; userInteracted: boolean }>({
    active: false, lastX: 0, userInteracted: false,
  });

  // Pointer handlers on the wrapping div.
  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    dragStateRef.current.active = true;
    dragStateRef.current.lastX = e.clientX;
    dragStateRef.current.userInteracted = true;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragStateRef.current.active) return;
    const dx = e.clientX - dragStateRef.current.lastX;
    dragStateRef.current.lastX = e.clientX;
    rotationYRef.current += dx * DRAG_SENSITIVITY;
  };
  const onPointerUp = (e: React.PointerEvent) => {
    (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
    dragStateRef.current.active = false;
  };

  return (
    <div
      style={{
        width, height,
        position: 'relative',
        cursor: 'grab',
        touchAction: 'none',
        userSelect: 'none',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <Canvas
        shadows={false}
        // Higher DPR + true MSAA — the Preview is small and static so the
        // pixel-budget can absorb it. Was [1, 1.25] + antialias=false which
        // produced the staircase silhouette the user complained about.
        dpr={[1.5, 2]}
        gl={{ antialias: true, powerPreference: 'high-performance' }}
        camera={{ position: [0, 0.6, 4.6], fov: 30, near: 0.1, far: 50 }}
        style={{ width: '100%', height: '100%', background: 'transparent' }}
        onCreated={({ gl }) => {
          const canvas = gl.domElement;
          canvas.addEventListener('webglcontextlost', (e) => e.preventDefault(), false);
        }}
      >
        {/* Studio backdrop + floor — frames the character like an inventory
            inspector instead of the previous flat radial gradient. */}
        <StudioBackdrop />
        <StudioFloor />

        {/* Three-point lighting:
              - key: warm front-top, bright (1.6)
              - fill: soft hemisphere top-light
              - rim: cool back-light to carve out the silhouette
              - kicker: low warm bounce so the legs read */}
        <ambientLight intensity={0.55} />
        <hemisphereLight args={['#dcecff', '#3a2c20', 0.75]} />
        <directionalLight position={[3, 4, 4]} intensity={1.4} castShadow />
        <directionalLight position={[-3.5, 3, -2.5]} intensity={1.4} color="#7dd3fc" />
        <directionalLight position={[0, 0.4, -3]} intensity={0.55} color="#ffd28a" />
        {glowColor && (
          <pointLight position={[0, 1, 1.5]} intensity={1.6} color={glowColor} distance={4} />
        )}
        <Suspense fallback={null}>
          <RotatingCharacter
            layers={layers}
            bodyPath={bodyPath}
            lightningInHand={lightningInHand}
            bodyColorTint={bodyTint?.color}
            bodyColorTintStrength={bodyTint?.strength}
            rotationRef={rotationYRef}
            dragStateRef={dragStateRef}
          />
        </Suspense>
      </Canvas>
    </div>
  );
}

interface RotatingCharacterProps {
  layers: ReturnType<typeof getMeshLayers>;
  bodyPath: string;
  lightningInHand: boolean;
  bodyColorTint?: string;
  bodyColorTintStrength?: number;
  rotationRef: { current: number };
  dragStateRef: { current: { active: boolean; lastX: number; userInteracted: boolean } };
}

/**
 * Wraps PlayerCharacter in a group whose Y rotation is updated each frame —
 * either from drag input or from a slow auto-spin until the user grabs it.
 * Adds subtle breathing on Y so the figure feels alive instead of static.
 */
function RotatingCharacter({
  layers, bodyPath, lightningInHand, bodyColorTint, bodyColorTintStrength,
  rotationRef, dragStateRef,
}: RotatingCharacterProps) {
  const groupRef = useRef<Group>(null);
  const innerRef = useRef<Group>(null);
  const elapsed = useRef(0);
  const [, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  useFrame((_, dt) => {
    if (!groupRef.current) return;
    if (!dragStateRef.current.active && !dragStateRef.current.userInteracted) {
      rotationRef.current += IDLE_TURN_SPEED * dt;
    }
    groupRef.current.rotation.y = rotationRef.current;

    // Breathing: 2 cm vertical sinus + tiny rotation wobble.
    elapsed.current += dt;
    if (innerRef.current) {
      const t = elapsed.current;
      innerRef.current.position.y = Math.sin(t * 1.8) * 0.02;
      innerRef.current.rotation.z = Math.sin(t * 1.6) * 0.012;
    }
  });

  return (
    <group ref={groupRef}>
      <group ref={innerRef}>
        <PlayerCharacter
          layers={layers}
          animation="idle"
          bodyPath={bodyPath}
          position={[0, -1, 0]}
          rotationY={0}
          stripWeapons
          lightningInHand={lightningInHand}
          bodyColorTint={bodyColorTint}
          bodyColorTintStrength={bodyColorTintStrength}
        />
      </group>
    </group>
  );
}

// ── Studio backdrop + floor ─────────────────────────────────────────────────

let cachedBackdropTexture: CanvasTexture | null = null;

/** Procedural vertical-gradient backdrop sphere (no asset shipped). */
function StudioBackdrop() {
  const texture = useMemo(() => {
    if (cachedBackdropTexture) return cachedBackdropTexture;
    const canvas = document.createElement('canvas');
    canvas.width = 4;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new CanvasTexture(canvas);
    const grad = ctx.createLinearGradient(0, 0, 0, 256);
    grad.addColorStop(0.00, '#2a3858');   // upper sky — cool blue
    grad.addColorStop(0.45, '#152030');   // mid — deep navy
    grad.addColorStop(0.85, '#0a0f18');   // floor edge — near-black
    grad.addColorStop(1.00, '#050709');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 4, 256);
    const tex = new CanvasTexture(canvas);
    tex.needsUpdate = true;
    cachedBackdropTexture = tex;
    return tex;
  }, []);

  return (
    <mesh>
      <sphereGeometry args={[20, 16, 16]} />
      <meshBasicMaterial map={texture} side={BackSide} toneMapped={false} />
    </mesh>
  );
}

/** Soft circular floor with a fresnel-faded edge — sells "the character is
 *  standing on something" without needing a real reflection probe. */
function StudioFloor() {
  return (
    <group position={[0, -1.0, 0]}>
      {/* Outer dark disc */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.001, 0]}>
        <circleGeometry args={[2.2, 64]} />
        <meshBasicMaterial color="#0c1220" toneMapped={false} />
      </mesh>
      {/* Inner glow ring — accent-cyan, low opacity, sits 1 mm above the disc */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[1.18, 1.30, 64]} />
        <meshBasicMaterial color="#36e8ff" transparent opacity={0.45} toneMapped={false} />
      </mesh>
      {/* Inner soft disc (lit floor patch right under the feet) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.001, 0]}>
        <circleGeometry args={[1.16, 48]} />
        <meshBasicMaterial color="#1a2840" transparent opacity={0.7} toneMapped={false} />
      </mesh>
    </group>
  );
}
